import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import styles from './user-save-conflict-popup.module.scss';
import {EditorType} from '@Utils/misc.util';
import {Button, Size, Type} from '@Components/button';
import {openUserSaveConflictModal} from '@Modals/user-save-conflict-modal';
import {useIsInModal} from '@Components/modal/hooks/useIsInModal';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {escapeHTML} from '@Utils/string.util';

export interface UserSaveConflictPopupProps {
  currentUserPictureURL: string;
  otherUserPictureURL: string;
  otherUserName: string;
  editorType: EditorType;
  onAcceptYours: VoidFunction;
  onAcceptTheirs: VoidFunction;
  openModalFromTabletSize?: boolean;
}

export function UserSaveConflictPopup({openModalFromTabletSize = true, ...props}: UserSaveConflictPopupProps): ReactElement<any> {
  const isMobileWidth = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MEDIUM);
  const isInModal = useIsInModal();
  const escapedOtherUserName = escapeHTML(props.otherUserName);

  const getEditorTextByType = (editorType: EditorType): string => {
    let text = '';
    switch (editorType) {
      case EditorType.EMAIL:
        text = window.i18next.t('pmwjs_email');
        break;
      case EditorType.DESIGN:
        text = window.i18next.t('pmwjs_design');
        break;
      case EditorType.WEBSITE:
        text = window.i18next.t('pmwjs_website');
        break;
      case EditorType.FORM:
        text = window.i18next.t('pmwjs_form');
        break;
      default:
        text = '';
    }

    return '<span class="-lowercase">' + text + '</span>';
  };

  const getYoursButtonTextHTML = (): string => {
    const text = window.i18next.t('pmwjs_user_save_your_progress');

    return getImageWithTextHTMLAsString(text, props.currentUserPictureURL);
  };

  const getTheirsButtonTextHTML = (): string => {
    const text = window.i18next.t('pmwjs_user_save_x_progress', {name: escapedOtherUserName});

    return getImageWithTextHTMLAsString(text, props.otherUserPictureURL);
  };

  const getDescriptionText = (): string => {
    return window.i18next.t('pmwjs_user_made_some_changes_just_now', {name: escapedOtherUserName, editor: getEditorTextByType(props.editorType)});
  };

  const getAcceptYoursInstruction = (): string => {
    return window.i18next.t('pmwjs_user_save_accept_yours', {name: escapedOtherUserName});
  };

  const getAcceptTheirsInstruction = (): string => {
    return window.i18next.t('pmwjs_user_save_accept_theirs');
  };

  const getImageWithTextHTMLAsString = (text: string, imageURL: string): string => {
    return `<div class="flex-h-row flex-center gap-2">
    <img src="${imageURL}" alt="" aria-hidden="true" class="${styles.roundedBtnImage}" />
    <span>${text}</span>
    </div>`;
  };

  const getCTAs = (): ReactElement<any> => {
    return (
      <>
        <Button
          type={Type.SECONDARY}
          customClasses={'-noun'}
          onClick={props.onAcceptTheirs}
          dangerouslySetTextAsHTML
          size={Size.SMALL}
          isFullWidth
          text={getTheirsButtonTextHTML()}
        />
        <Button
          type={Type.SECONDARY}
          customClasses={'-noun'}
          onClick={props.onAcceptYours}
          dangerouslySetTextAsHTML
          size={Size.SMALL}
          isFullWidth
          text={getYoursButtonTextHTML()}
        />
      </>
    );
  };

  const getDescription = (): ReactElement<any> => {
    return (
      <Text val={getDescriptionText()} dangerouslySetInnerHTML size={TextSize.XSMALL} className={'content-body'} />
    );
  };

  const getInstructionsList = (): ReactElement<any> => {
    return (
      <ul className={styles.list}>
        <li className={styles.listItem}>
          <Text val={getAcceptYoursInstruction()} dangerouslySetInnerHTML size={TextSize.XSMALL} className={'content-body'} />
        </li>
        <li className={styles.listItem}>
          <Text val={getAcceptTheirsInstruction()} dangerouslySetInnerHTML size={TextSize.XSMALL} className={'content-body'} />
        </li>
      </ul>
    );
  };

  const shouldShowMobileModal = (): boolean => {
    return openModalFromTabletSize && isMobileWidth && !isInModal;
  };

  const getMobileCTAClasses = (): string => {
    const classes = 'gap-3 flex-center';

    if (isMobileWidth) {
      return `flex-v-row ${classes}`;
    }

    return `flex-h-row ${classes}`;
  };

  if (shouldShowMobileModal()) {
    openUserSaveConflictModal(props);
    return <></>;
  }

  return (
    <div className={`${styles.popup} flex-v-row gap-6 spacing-p-4`}>
      {getDescription()}
      <div className={styles.grayContainer}>{getInstructionsList()}</div>
      <div className={getMobileCTAClasses()}>{getCTAs()}</div>
    </div>
  );
}
